import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";
import { Reveal } from "../components/motion/Reveal";
import { RainbowArcs } from "../components/motion/RainbowArcs";
import { PrayerCard } from "../components/daily/PrayerCard";
import { DAILY_PRACTICES } from "../data/dailyLife";

const Practice = ({ p, index }) => {
  const flip = index % 2 === 1;
  return (
    <section
      id={p.id}
      data-testid={`daily-practice-${p.id}`}
      className={`scroll-mt-28 border-t hairline py-16 lg:py-24 ${flip ? "bg-parchment-deep/60" : ""}`}
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-5 sm:px-8 lg:grid-cols-12">
        <div className={`lg:col-span-4 ${flip ? "lg:order-2 lg:col-start-9" : ""}`}>
          <Reveal>
            <span className="font-serif text-7xl leading-none text-terracotta sm:text-8xl">{p.number}</span>
            <h2 className="mt-6 font-serif text-4xl leading-[1.05] tracking-tight sm:text-5xl">{p.title}</h2>
            <p className="mt-3 font-serif text-xl italic text-ink-soft">{p.subtitle}</p>
          </Reveal>
        </div>
        <div className={`lg:col-span-7 ${flip ? "lg:order-1 lg:col-start-1" : "lg:col-start-6"}`}>
          <Reveal delay={0.1}>
            <p className="max-w-2xl text-base leading-[1.8] text-ink-soft sm:text-lg">{p.body}</p>
            <ul className="mt-10 space-y-4">
              {p.practices.map((item, i) => (
                <li key={i} className="flex items-start gap-4 border-t hairline pt-4">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-terracotta/10 text-terracotta">
                    <Check size={12} strokeWidth={2.5} />
                  </span>
                  <p className="text-sm leading-relaxed text-ink sm:text-base">{item}</p>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default function DailyLife() {
  return (
    <div data-testid="daily-life-page">
      <PageHeader
        testId="daily-life-header"
        eyebrow="Daily Life & Guidance"
        lines={["One person", "can change", "the world."]}
        lede={
          <>
            <span className="block font-serif text-2xl italic leading-snug text-ink sm:text-3xl">
              A single thought, word or action can have an immeasurable effect on the whole world.
            </span>
            <span className="mt-5 block">
              The Sages taught that each person should see the world as perfectly balanced — so that
              one good deed tips the entire scale toward good. Nothing below is too small to
              matter. In this way an ordinary person, on an ordinary day, in the quiet of their own
              life, moves the whole world a little closer to what it was made to be.
            </span>
          </>
        }
      />

      <nav
        aria-label="Practices"
        className="mx-auto mb-4 flex max-w-7xl flex-wrap gap-x-7 gap-y-3 px-5 sm:px-8"
      >
        {DAILY_PRACTICES.map((p) => (
          <a
            key={p.id}
            href={`#${p.id}`}
            data-testid={`daily-jump-${p.id}`}
            className="link-underline flex items-baseline gap-2 text-sm text-ink-soft hover:text-ink"
          >
            <span className="font-mono text-[10px] text-terracotta">{p.number}</span>
            {p.title}
          </a>
        ))}
        <a
          href="#modeh-ani"
          data-testid="daily-jump-modeh-ani"
          className="link-underline flex items-baseline gap-2 text-sm text-ink-soft hover:text-ink"
        >
          <span className="font-mono text-[10px] text-terracotta">10</span>
          Modeh Ani — a morning prayer
        </a>
        <a
          href="#bedtime"
          data-testid="daily-jump-bedtime"
          className="link-underline flex items-baseline gap-2 text-sm text-ink-soft hover:text-ink"
        >
          <span className="font-mono text-[10px] text-terracotta">11</span>
          Before sleep — an evening prayer
        </a>
      </nav>

      <div className="mt-12">
        {DAILY_PRACTICES.map((p, i) => (
          <Practice key={p.id} p={p} index={i} />
        ))}
      </div>

      <section
        id="modeh-ani"
        data-testid="daily-modeh-ani"
        className="relative overflow-hidden border-t hairline bg-parchment-deep/70 py-24 lg:py-32"
      >
        <RainbowArcs
          animate={false}
          className="pointer-events-none absolute -bottom-16 left-1/2 w-[120vw] max-w-[1200px] -translate-x-1/2 text-terracotta/10"
        />
        <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-12 px-5 sm:px-8 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Reveal>
              <p className="eyebrow">A prayer to begin the day</p>
              <h2 className="mt-5 font-serif text-4xl leading-[1.05] tracking-tight sm:text-5xl">
                Modeh Ani
                <br />
                <span className="italic text-ink-soft">מוֹדֶה אֲנִי</span>
              </h2>
              <p className="mt-6 max-w-sm text-base leading-relaxed text-ink-soft">
                The first words a Jew says on waking, before even getting out of bed — and a prayer
                any person can make their own. It contains no Name of G-d, so it may be said
                immediately, with eyes barely open. It takes four seconds, and it sets the tone of
                a day: I am here, I was given this, thank You.
              </p>
              <p className="mt-4 text-xs text-ink-muted">
                A woman says <em>Modah ani</em> (מוֹדָה אֲנִי).
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-7 lg:col-start-6">
            <Reveal delay={0.1}>
              <PrayerCard
                id="modeh-ani"
                hebrew="מוֹדֶה אֲנִי לְפָנֶיךָ מֶלֶךְ חַי וְקַיָּם, שֶׁהֶחֱזַרְתָּ בִּי נִשְׁמָתִי בְּחֶמְלָה. רַבָּה אֱמוּנָתֶךָ."
                transliteration="Modeh ani lefanecha, Melech chai v'kayam, shehechezarta bi nishmati b'chemlah. Rabbah emunatecha."
                english="I give thanks before You, living and enduring King, for You have returned my soul within me with compassion. Great is Your faithfulness."
                notes={[
                  { k: "Modeh ani", v: "'I give thanks' — the day begins with gratitude, not with need." },
                  { k: "Shehechezarta bi nishmati", v: "'You returned my soul to me' — sleep is a small surrender; waking is a gift given again." },
                  { k: "Rabbah emunatecha", v: "'Great is Your faithfulness' — He trusted me with another day. The question is what I will do with it." },
                ]}
              />
            </Reveal>
          </div>
        </div>
      </section>

      <section
        id="bedtime"
        data-testid="daily-bedtime"
        className="relative overflow-hidden border-t hairline bg-ink py-24 text-parchment lg:py-32"
      >
        <RainbowArcs
          animate={false}
          className="pointer-events-none absolute -top-10 right-[-10%] w-[70vw] max-w-[900px] rotate-180 text-terracotta/20"
        />
        <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-12 px-5 sm:px-8 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Reveal>
              <p className="eyebrow">A prayer to end the day</p>
              <h2 className="mt-5 font-serif text-4xl leading-[1.05] tracking-tight sm:text-5xl">
                Before sleep
                <br />
                <span className="italic text-parchment/60">בְּשָׁלוֹם</span>
              </h2>
              <p className="mt-6 max-w-sm text-base leading-relaxed text-parchment/70">
                If Modeh Ani opens the day, this closes it. A single verse from the Psalms, said in
                the dark before sleep: a handing-back of the day, and of yourself, to the One who
                kept you through it. Many add the last line of the ancient hymn Adon Olam.
              </p>
              <p className="mt-4 text-xs text-parchment/50">
                Both texts are universal — written for every person who lies down at night.
              </p>
            </Reveal>
          </div>

          <div className="space-y-12 lg:col-span-7 lg:col-start-6">
            <Reveal delay={0.1}>
              <div className="[&_.bg-parchment]:bg-[#262220] [&_.bg-parchment]:text-parchment [&_.text-ink]:text-parchment [&_.text-ink-soft]:text-parchment/70">
                <PrayerCard
                  id="psalm-4"
                  hebrew="בְּשָׁלוֹם יַחְדָּו אֶשְׁכְּבָה וְאִישָׁן, כִּי אַתָּה ה' לְבָדָד לָבֶטַח תּוֹשִׁיבֵנִי."
                  transliteration="B'shalom yachdav eshkevah v'ishan, ki atah Hashem l'vadad lavetach toshiveni."
                  english="In peace I will both lie down and sleep, for You alone, Lord, make me dwell in safety."
                  notes={[
                    { k: "Psalm 4:9", v: "King David's last words before sleep in a night of trouble — and still the traditional verse to close the day." },
                    { k: "B'shalom", v: "'In peace' — not because everything is resolved, but because it has been handed over." },
                    { k: "L'vadad lavetach", v: "'Alone, in safety' — the security comes from one source only." },
                  ]}
                />
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="[&_.bg-parchment]:bg-[#262220] [&_.bg-parchment]:text-parchment [&_.text-ink]:text-parchment [&_.text-ink-soft]:text-parchment/70">
                <PrayerCard
                  id="adon-olam"
                  hebrew="בְּיָדוֹ אַפְקִיד רוּחִי, בְּעֵת אִישַׁן וְאָעִירָה. וְעִם רוּחִי גְּוִיָּתִי, ה' לִי וְלֹא אִירָא."
                  transliteration="B'yado afkid ruchi, b'et ishan v'a'irah. V'im ruchi g'viyati, Hashem li v'lo ira."
                  english="Into His hand I entrust my spirit, when I sleep and when I wake; and with my spirit, my body too. The Lord is with me — I shall not fear."
                  notes={[
                    { k: "Adon Olam, closing line", v: "The end of a hymn sung for a thousand years, built on Psalm 31:6: 'Into Your hand I entrust my spirit.'" },
                    { k: "Afkid ruchi", v: "'I entrust my spirit' — a deposit, given to a trustworthy keeper, to be returned in the morning." },
                    { k: "V'lo ira", v: "'I shall not fear' — the last thought before sleep, and the first strength on waking." },
                  ]}
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-t hairline bg-parchment py-20 lg:py-28">
        <div className="mx-auto flex max-w-7xl flex-col gap-8 px-5 sm:px-8 lg:flex-row lg:items-end lg:justify-between">
          <Reveal>
            <p className="eyebrow">Keep going</p>
            <h2 className="mt-5 max-w-xl font-serif text-4xl leading-[1.05] tracking-tight sm:text-5xl">
              Questions tend to come next. <span className="italic text-ink-soft">That's a good sign.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/faq"
              data-testid="daily-life-faq-link"
              className="group inline-flex items-center gap-3 rounded-full bg-ink px-7 py-4 text-sm font-medium text-parchment transition-colors duration-300 hover:bg-terracotta"
            >
              Browse the FAQ
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
